import { CalendarSyncConnectorType, ShiftStatus, ShiftType } from '@/types';
import { Dayjs } from 'dayjs';

export type ActionState = {
  id: string;
  name: string;
  actionTypeId: string;
  isFinal: boolean;
  requiresApproval: boolean;
  sequence: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type Action = {
  id: string;
  actionStateId: string;
  userId: string;
  referenceId: string | null;
  actionTime: string;
  shiftMarker: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  state: string;
  actionTypeId: string;
  shiftSequence?: number;
  actionState?: string;
  entityId?: string;
  jobSubType?: string;
  employeeId?: string;
};

export type PTOShift = {
  id: string;
  employeeId: string;
  calendarId: string;
  requestType: string;
  requestStatus: 'pending' | 'approved' | 'rejected';
  requestedToId: string | null;
  reason: string;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  updatedBy: string;
  history?: [
    {
      id: string;
      reason: string;
      createdAt: string;
      updatedAt: string;
      updatedBy: string;
      archivedAt: string | null;
      calendarId: string;
      employeeId: string;
      requestType: string;
      requestStatus: string;
      requestedToId: string | null;
    }
  ];
  calendar: {
    endTime: string;
    startTime: string;
  };
  employee: {
    id: string;
    firstName: string;
    lastName: string;
  };
};

export type CalendarShifts = {
  allDay: boolean;
  customerId?: string;
  customerName?: string | null;
  employee?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    backgroundColor?: string | null;
    textColor?: string | null;
  };
  jobRolesTitles?: string[];
  recurrence?: { rule: string | null };
  endTime: string;
  entityId: string;
  hasShiftRequests: number | boolean;
  id: string;
  startTime: string;
  timezone: string;
  totalShiftApplies: number;
  type: ShiftType;
  uiType: string;
  hasOverlap?: boolean;
  statusInfo: {
    status: 'Filled' | 'In Review' | 'Not Yet Reviewed' | 'No Applicants' | 'Awaiting Acknowledgement';
    message?: string;
  };
  baseRate?: number | null;
  acceptedRate?: number | null;
  averageRate?: number | null;
}[];

export type CalendarShift = {
  allDay: boolean;
  customer?: Customer;
  description?: string | null;
  employee?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    backgroundColor?: string | null;
    textColor?: string | null;
  };
  endTime: string;
  entityId: string;
  entityName?: string | null;
  id: string;
  capacityRequestId?: string | null;
  jobRole?: {
    id?: string | null;
    title?: string | null;
    category: string;
    jobRoleCategoryId: string;
    jobRoleTitleId?: string;
  };
  licensedStates?: string[] | null;
  recurrence?: { rule: string | null };
  services?: Array<{ id: string; name: string }> | null;
  shiftDetails: {
    long: string;
    short: string;
    email: string;
    sms: string;
  };
  shiftRequests?: ShiftRequest[] | null;
  skills?: Skill[];
  startTime: string;
  tags: string[] | null;
  timezone: string;
  title: string;
  type: ShiftType;
  bonus?: string | null;
  baseRate?: number | null;
  acceptedRate?: number | null;
  averageRate?: number | null;
};

export interface CapacityRequest {
  key: string;
  id: string;
  entityId: string;
  roles: string[];
  rules: Record<'entity' | 'states' | 'jobRole' | 'services' | 'jobRoleCategory', string[]>;
  services: string[];
  numberOfSlots: number;
  filledSlots: number;
  startTime: string;
  endTime: string;
  remainingCapacity: number;
  tags: string[] | null;
  filledRatio: number;
  maxClinicians: number;
}

export interface CapacityRequestByIdResponse {
  archivedAt: string | null;
  createdAt: string;
  endTime: string;
  entityId: string;
  entityName: string;
  filledSlots: number;
  id: string;
  licensedStates: string[] | null;
  numberOfSlots: number;
  roles: string[];
  rules: Record<'entity' | 'states' | 'jobRole' | 'services' | 'jobRoleCategory', string[]>;
  services: Service[];
  startTime: string;
  status: number;
  updatedAt: string;
}

export type ShiftRequest = {
  reason?: string;
  requestByName: string;
  requestEmployeeId: string;
  requestId: string;
  requestStatus: ShiftRequestStatus;
  requestType: string;
  requestedToId: string;
  requestedToName: string;
  requestCreatedAt?: string;
  updatedById?: string;
  updatedByName?: string;
  rate?: number;
  baseRate?: number;
  maxRate?: number;
  averageRate?: number;
};

export type Break = {
  breakEnd: number;
  breakId: string;
  breakStart: number;
};

export type EmployeeT = {
  id: string;
  entityId: string;
  firstName: string;
  lastName: string;
  managerId: string;
  title?: string | null;
  userId: string;
  email?: string | null;
};

export type EmployeeTUnique = Omit<EmployeeT, 'entityId'> & {
  entities: string[];
};

// For returning either EmployeeT or EmployeeTUnique
export type FetchEmployeesResponse<T extends EmployeeT | EmployeeTUnique> = {
  employees: T[];
};

export type Entity = {
  description: string;
  id: string;
  imgUrl: string;
  metadata: any;
  name: string;
};

export type EntityOption = {
  label: string;
  value: string;
  key: string;
};

export interface BaseFilterInput {
  startTime?: string;
  endTime?: string;
  entityIds?: string[];
  tz?: string;
  sortBy?: string;
  sortOrder?: string;
  size?: number;
  page?: number;
  count?: number;
}

export interface FetchCalendarShiftsInput extends BaseFilterInput {
  types?: ShiftType[];
  statuses?: ShiftStatus[];
  tags?: string[];
  states?: string[];
  people?: string[];
  customerIds?: string[];
  onlyAlerts?: boolean;
  onlyUnassigned?: boolean;
  presetParameter?: string;
  onlyUnfilled?: boolean;
  excludeTimeOff?: boolean;
}

export type SchedulingFiltersFormT = {
  checkboxItems?: string[];
  dateRange?: [Dayjs, Dayjs];
  entityIds?: string[];
} & FetchCalendarShiftsInput;

export interface FetchTimekeepingInput extends BaseFilterInput {
  people?: string[];
  jobTypes?: string[];
}

export type TimekeepingFiltersFormT = {
  dateRange?: [Dayjs, Dayjs];
  entityIds?: string[];
} & Omit<FetchTimekeepingInput, 'startTime' | 'endTime'>;

export interface FetchCapacityRequestsInput extends BaseFilterInput {
  statesIds?: string[];
  servicesIds?: string[];
  jobRoleIds?: string[];
  status?: number;
}

export type SchedulingFilterInput = FetchCalendarShiftsInput & FetchCapacityRequestsInput;

export interface ComplianceFilterInput extends BaseFilterInput {
  documentTypeIds?: string[];
  expired?: boolean;
  approvedBy?: string[];
  people?: string[];
}

export type EmployeeOptionsByEntityProps = {
  managerId?: string;
  entityId?: string | null;
  unique?: boolean;
  includeTaggedEntityEmployees?: boolean;
  eligible?: boolean;
  states?: string[] | null;
  services?: string[] | null;
  jobRoleId?: string;
  customerId?: string;
  locationId?: string;
};

export interface FetchCSVInput extends FetchCalendarShiftsInput {
  csv?: boolean;
  csvTimeline?: boolean;
  tz?: string;
}

export type FetchTimekeepingCSVInput = {
  tz: string;
  type?: 'timeline';
} & FetchTimekeepingInput;

export type ShiftAction = {
  id: string;
  actionTime: string;
  stateId: string;
  stateName: 'Break-In' | 'Break-Out' | 'Clock-In' | 'Clock-Out';
  stateOrder: number;
  isFinal: boolean;
  requestStatus: ShiftRequestStatus | null;
};

export enum ShiftRequestStatus {
  Approved = 'approved',
  Pending = 'pending',
  Rejected = 'rejected',
  Withdrawn = 'withdrawn',
  InReview = 'in_review',
  AwaitingAcknowledgement = 'awaiting_acknowledgement',
}

export enum ActionStateSequence {
  ClockIn = 1,
  BreakIn,
  BreakOut,
  ClockOut,
}

export type TimeKeeping = {
  actions: Array<ShiftAction>;
  clockInTime: string;
  clockOutTime: string | null;
  employee: {
    id: string;
    userId: string;
    firstName?: string;
    lastName?: string;
  };
  entityId: string;
  jobRole: {
    id: string;
    category: string;
    title: string;
  };
  jobSubType: string;
  locationId: string;
  shiftSequence: number;
};

export type TimeKeepingGraphData = {
  time: string;
  timeLabel: string;
  count: number;
}[];

export type ScheduleGraphData = {
  time: string;
  workers: number;
}[];

export type CreateActionInput = {
  actionStateId: string;
  userId: string;
  employeeId?: string;
  actionTime?: string;
  location?: string;
  actionJobSiteId?: string;
  shiftMarker?: boolean;
  locationId?: string;
  entityId?: string | null;
  jobRoleId?: string;
  jobSubType?: string;
  shiftSequence?: number;
  referenceId?: string;
};

export type UpdateActionInput = {
  id: string | null;
  actionStateId: string;
  userId: string;
  actionTime?: string;
  location?: string;
  actionJobSiteId?: string;
  shiftMarker?: boolean;
  locationId?: string;
  entityId?: string;
  jobRoleId?: string;
  jobSubType?: string;
  shiftSequence?: number;
  referenceId?: string;
};

export type JobRole = {
  category: string;
  entity_id: string;
  id: string;
  is_active: boolean;
  title: string;
  job_role_category_id: string;
  job_role_title_id: string;
};

export type ActionType = {
  created_at: string;
  deleted_at: string | null;
  id: string;
  name: string;
  requires_approval: boolean;
  updated_at: string;
};

export type Employee = {
  id: string;
  userId: string;
  managerId: string;
  firstName: string | null;
  lastName: string | null;
  backgroundColor?: string | null;
  textColor?: string | null;
};

export type ApprovalRequest = {
  id: string;
  actionId: string;
  employeeId: string;
  requestType: string;
  requestStatus: 'pending' | 'approved' | 'rejected';
  reason: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  updatedBy: string;
  employee: Employee;
  manager: Employee;
  actions: Action[];
};

export enum ResourceUIType {
  Schedules = 'schedules',
  Capacity = 'capacity',
  Timekeeping = 'timekeeping',
  Availability = 'availability',
  Events = 'events',
  Approvals = 'approvals',
  Notifications = 'notifications',
  Insights = 'insights',
  Users = 'users',
  Earnings = 'earnings',
  Connectors = 'connectors',
  Plans = 'plans',
  Dashboard = 'dashboard',
  Employee = 'employee',
  Rewards = 'rewards',
  Performance = 'performance',
  Widgets = 'widgets',
  AdminDashboard = 'admin_dashboard',
  Compliance = 'compliance',
  OnboardingAgent = 'onboarding_agent',
}

export type EmployeePermissions = {
  resource: ResourceUIType;
  can: Array<'read' | 'delete' | 'update' | 'create'>;
};

export type CurrentEmployee = {
  id: string;
  employeeId?: string | null;
  entityId?: string | null;
  firstName: string;
  isManager: boolean;
  isAdmin: boolean;
  managerId: string | null;
  lastName: string;
  permissions: EmployeePermissions[];
};

export type BulkShiftUploadReturn = {
  document: {
    id: string;
  };
  shifts: Array<
    Record<
      'description' | 'endTime' | 'entity' | 'shiftType' | 'startTime' | 'tags' | 'user' | 'bonus',
      BulkShiftsUploadObjectItem
    >
  >;
};

export type BulkShiftsUploadObjectItem = {
  id?: string;
  value: string;
  validationMessage?: string;
};

export type Customer = {
  id: string;
  firstName: string;
  lastName: string;
  address: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  latlng?: { type: string; coordinates: [number, number] };
  metadata?: Record<string, any>;
  distance?: number | null;
  drivingDistance?: number | null;
  drivingTime?: number | null;
};

export type Service = {
  id: string;
  name: string;
  job_role_id?: string;
  metadata?: any;
  external_id: string;
};

export type Skill = {
  id: string;
  name: string;
};

export type CalendarShiftRequest = {
  id: string;
  calendarId: string;
  employeeId: string;
  groupId?: string | null;
  requestStatus: ShiftRequestStatus;
  requestType: string;
  reason?: string | null;
  requestedToId?: string | null;
};

export type EmployeeCalendarShift = {
  allDay: boolean;
  bonus?: string | null;
  baseRate?: string | null;
  acceptedRate?: string | null;
  averageRate?: string | null;
  description?: string | null;
  title?: string | null;
  employee?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
  };
  services?: Service[];
  skills?: Skill[];
  endTime: string;
  id: string;
  entityId: string;
  shiftRequests?: Array<{
    calendarId: string;
    employeeId: string;
    groupId?: string | null;
    id: string;
    requestStatus: ShiftRequestStatus;
    requestType: string;
    reason?: string | null;
    requestedToId?: string | null;
  }>;
  startTime: string;
  tags: string[];
  timezone: string;
  type: ShiftType;
  requestId?: string | null;
  entityName?: string | null;
  customer?: Customer;
  licensedStates?: string[] | null;
  jobRole?: {
    id: string;
    title: string;
    category: string;
  };
  detailsShort?: string;
  shiftDetails?: {
    long: string;
    short: string;
    email: string;
    sms: string;
  };
};

export type QualityParam = {
  id: string;
  name: string;
  weight: number;
  version: number;
  isPositive: boolean;
  formulaSteps?: {
    formulas?: { qualityData: Record<string, string[]>; scoreParam: string[] };
  };
  qualityParamVersionId: string;
};

export type AryaScoreVersion = {
  id: string;
  name: string;
  version: number;
  isCurrent: boolean;
  description: string;
  formula: string[];
  qualityParams: Array<QualityParam>;
};

export type UpdateScoreInput = { id: string; qualityParams: { id: string; weight: number }[] };

export type AryaScore = {
  aryaScore: number;
  employeeId: string;
  employeeName: string;
  change: number;
  baseScore: number;
  scoreParams: {
    isPositive: boolean;
    qualityParamId: string;
    qualityParamName: string;
    scoreParamId: string;
    scoreParamTimestamp: string;
    scoreParamValue: number;
    change: number;
  }[];
  timestamp: string;
};

export type ScoreParam = {
  qualityParamId: string;
  qualityParamName: string;
  isPositive: boolean;
  name: string;
} & Record<
  string,
  {
    month: string;
    scoreAvg: number;
    scoreCount: number;
    scoreSum: number;
    timestamp: string;
    week: number;
  }
>;

export type AryaScoreHistory = {
  date: string;
  month: string;
  scoreAvg: number;
  scoreMax: number;
  scoreMin: number;
  scoreSum: number;
  version: number;
};

export type EmployeeScoreHistory = {
  timestamp: string;
  executionId: string;
  date?: string;
  scoreAvg: number;
  scoreMax: number;
  scoreMin: number;
  scoreSum: number;
  version: number;
};

export type UserContext = {
  userContext: {
    userId?: string;
    employeeId?: string;
    orgId?: string;
    userEmail?: string;
  };
};

export type DemandDateEntry = {
  totalResourceNumber: number;
  values: Array<{
    entityName: string;
    resourceNumber: string;
    startTime?: string;
    endTime?: string;
    tags?: string[];
  }>;
};

export type DemandDataUpload = {
  columns: string[];
  rows: string[];
  demandData: Array<{
    row: string;
    [key: string]: string | DemandDateEntry;
  }>;
  demandId: string;
  summary: Array<{ confirmMessage: string; totalResourcesNeeded: number }>;
};

export type CalendarEligibleEmployeesInput = {
  entityId?: string | null;
  states?: string[] | null;
  services?: string[] | null;
  jobRoleId?: string | null;
  customerId?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  employeeId?: string | null;
  jobRoleCategoryId?: string | null;
  calendarId?: string | null;
  forShiftTypes?: string[] | null;
};

export type CalendarSyncConnector = {
  id: string;
  calendarId?: string;
  url?: string;
  employee_id?: string;
  lastSyncTime?: string;
  metadata: { isActive: boolean; type?: CalendarSyncConnectorType; filters?: Partial<FetchCalendarShiftsInput> };
};

export type EmployeeLicense = {
  acquired_at: string;
  expires_at: string;
  identifier: string;
  license: string;
  license_id: string;
  state: string;
  state_id: string;
  state_name: string;
  verified_at: string;
  verified_by: string;
  verified_by_first_name?: string;
  verified_by_last_name?: string;
  archived_at: string | null;
};

export type ProfileJobRole = {
  category_name: string;
  category_id: string;
  entity_id: string;
  entity_name: string;
  entity_tags: string;
  job_role_id: string;
  title: string;
  title_id: string;
  location_name: string;
  location_id: string;
  entity_employment_id: string;
};

export type EmployeeProfileResponse = {
  id: string;
  firstName: string;
  lastName: string;
  title: string | null;
  metadata: {
    bio: string;
    licenses: string[];
    clinician_NPI: string;
    specialties: string;
  };
  employeeProfile: EmployeeProfilePayload;
  licenses: EmployeeLicense[];
  jobRoles: ProfileJobRole[];
};

export interface EmployeeProfilePayload {
  first_name?: string;
  last_name?: string;
  preferred_name?: string | null;
  preferred_pronoun?: string | null;
  dob?: string | null;
  gender?: string | null;
  date_joined?: string | null;
  email?: string;
  mobile?: string;
  ok_to_text?: boolean;
  ok_to_email?: boolean;
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  country?: string | null;
}

export type EmployeeAvailability = {
  id: string;
  employee_id: string;
  availability_type: 'available' | 'unavailable';
  available_from: string;
  available_to: string;
  recurrence_pattern: string;
  available_minutes: number;
  archived_at: string | null;
  metadata: object;
  created_at: string;
  updated_at: string;
};

export interface FetchAvailabilityInput {
  filters: {
    employeeIds?: string[];
    startTime?: string;
    endTime?: string;
    entityId?: string | null;
    sortField?: string;
    sortOrder?: string;
    pageNumber?: number;
    pageSize?: number;
    tz?: string;
    onlyEngaged?: boolean;
  };
}

export type EmployeeAvailabilityOutput = {
  employee: {
    id: string;
    firstName: string;
    lastName: string;
  };
  hoursavailable?: string | null;
  hoursworked?: string | null;
  lastshift?: string | null;
  nextavailable?: string | null;
  nextApprovedTimeoff?: {
    startTime: string;
    endTime: string;
    id: string;
    type: string;
  };
  nextApprovedTimeoffAt?: string | null;
  nextApprovedTimeoffHours?: number | null;
  pendingTimeoffs: Array<{
    calendar_id: string;
    employee_id: string;
    end_time: string;
    hours: string;
    reason: string;
    request_status: string;
    start_time: string;
    timeoff_type: string;
  }>;
};

export type AvailabilityOutput = {
  id: string;
  availability_type: 'available' | 'unavailable';
  available_from: string;
  available_to: string;
  employee: {
    id: string;
    firstName: string;
    lastName: string;
  };
  hasOverlap?: boolean;
};

export type CreateAvailabilityInput = {
  employee_id: string;
  availability_type: string;
  available_from: string;
  available_to: string;
};

export type DashboardEndpoints = 'scheduling' | 'timesheet' | 'approval' | 'availability' | 'capacity';

export interface DashboardFilters {
  startTime?: string;
  endTime?: string;
  tags?: string[];
  entityIds?: string[];
  people?: string[];
  onlyShiftRequests?: boolean;
}

export interface EntityFilters {
  entityIds?: string[];
  employeeIds?: string[];
}

export enum PreferenceCategoryType {
  Filters = 'filters',
  WorkPreference = 'work_preference',
  UISettings = 'ui_settings',
  Notifications = 'notifications',
}

export type EmployeePreference = {
  category: PreferenceCategoryType;
  dataType: 'text' | 'number' | 'time' | 'boolean' | 'json';
  id: string;
  name: string;
  value: string;
};

export interface AllCustomersType {
  total: number;
  pages: number;
  rows: Customer[];
}

export type ActivityLog = {
  employeeId: string;
  firstName: string;
  lastName: string;
  totalScore: number;
  notifyAt: string;
  notificationsInfo: Array<{
    id: string;
    createdAt: string;
    resourceType: string;
    triggeredByService: string;
  }>;
  jobRolesTitles: string[];
};

export type CreateLocationPayload = {
  name: string;
  description: string;
  address: string;
  imgUrl: string;
};

type CreateEmployeePayload = {
  firstName: string;
  lastName: string;
  email: string;
};

export type BulkCreateEmployeePayload = {
  employees: CreateEmployeePayload[];
  jobRoleId: string;
  locationId: string;
};

export type Location = {
  id: string;
  name: string;
  description: string;
  imgUrl: string;
};

export type RolesResponse = {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type EmployeeResponse = {
  employee_id: string;
  user_id: string;
  manager_id: string | null;
  first_name: string;
  last_name: string;
  title: string | null;
};

export type EmployeeRoleResponse = {
  employee: EmployeeResponse[];
  roles: Array<{ id: string; name: string }>;
};

export type License = {
  id: string;
  name: string;
  description: string;
  metadata: any;
};

export type AddEmployeeLicensePayload = {
  employeeId: string;
  stateId: string;
  licenseId: string;
  identifier: string;
  acquiredAt: string;
  expiresAt: string;
};

export interface Performer {
  score: number;
  firstName: string;
  lastName: string;
  employeeId: string;
}

export interface UnfilledShift {
  id: string;
  startTime: string;
  endTime: string;
  entityId: string;
  entityName: string;
  customerId: string | null;
  customerName: string;
}

export interface EngagedEmployee {
  employeeId: string;
  firstName: string;
  lastName: string;
  averageHoursPerWeek: number;
  percentageOfFullTime: number;
}

export interface TimeOffRequest {
  category: string;
  startTime: string;
  endTime: string;
  reason?: string;
}

export type TimeOffHistoryResponse = {
  endTime: string;
  id: string;
  reason: string | null;
  requestStatus: 'approved' | 'rejected' | 'pending';
  startTime: string;
  timeoffType: 'PTO' | 'sick' | 'unpaid';
  requestId: string;
  requestedAt: string;
};

export interface BulkApplyShiftObj {
  calendarId: string;
  type: string;
}

export interface BulkApplyPayload {
  calendars: BulkApplyShiftObj[];
  type: string;
  applyRequest: {
    toEmployeeId: string;
  };
}

export interface BulkApplySuccess {
  success: boolean;
  calendarId: string;
  type: string;
  result: {
    calendarId: string;
    employeeId: string;
    requestType: string;
    requestStatus: string;
    reason?: string;
  };
}

export interface BulkApplyError {
  success: boolean;
  calendarId: string;
  error: string;
  type: string;
}

export interface BulkApplyResponse {
  successes: BulkApplySuccess[];
  errors: BulkApplyError[];
}

export interface BulkApplyCapacity {
  id: string;
  date: string;
}

export type WorkingHourSlot = {
  startTime: string;
  endTime: string;
};

export type WorkingDay = {
  slots: WorkingHourSlot[];
  synchronousOnly: boolean;
};

export type EntitySettings = {
  workingHours: {
    dates: Record<string, WorkingDay>;
    monday: WorkingDay;
    tuesday: WorkingDay;
    wednesday: WorkingDay;
    thursday: WorkingDay;
    friday: WorkingDay;
    saturday: WorkingDay;
    sunday: WorkingDay;
  };
};

export interface EmployeesBulkArchivePayload {
  employees: string[];
  useExternalId?: boolean;
}

export type MultitenantUser = {
  id: string;
  email: string;
  fullName: string;
  organizationName: string;
  organizationId: string;
  withPassword: boolean;
};

export interface FetchBonusCapacityInput {
  startTime: string;
  endTime: string;
  entityId: string;
  employeeId: string;
}

export type BonusCapacity = {
  bonus: string | null;
  currEmployeeAssignedCount: string;
  endTime: string;
  entityId: string;
  filledSlots: number;
  id: string;
  jobRoleCategory: string;
  licensedStates: string[];
  maxClinicians: number | null;
  numberOfSlots: number;
  roles: string[];
  rules: { entity: string; states: string; jobRoleCategory: string };
  services: string[];
  startTime: string;
  tags: string[];
  uniqueEmployeesCount: string;
  visibilityStatus: number;
};

export enum DocumentApprovalAction {
  approve = 'APPROVE',
  reject = 'REJECT',
}

interface DateRange {
  startDate: string; // ISO date e.g. "2025-09-01"
  endDate: string; // ISO date e.g. "2025-12-15"
}

export interface Pattern {
  weekday: string;
  startTime: string;
  endTime: string;
  dateRange: DateRange;
}

export interface Opportunity {
  pattern: Pattern;
  skills?: string[];
  bonusAvailable?: boolean;
  shiftsAvailable?: number;
  tags?: string[];
  services?: string[];
  pendingApplications?: number;
  shiftsScheduled?: number;
  pendingAcknowledgements?: number;
}

interface Totals {
  shiftsAvailable?: number;
  pendingApplications?: number;
  shiftsScheduled?: number;
  pendingAcknowledgements?: number;
}

export interface PatientApiResult {
  id?: string;
  fullName: string;
  age?: number;
  gender?: string;
  distance?: number | null; // corresponds to "distance" in the old shape
  opportunities: Opportunity[];
  totals?: Totals;
  // optional flags returned elsewhere
  hasFlexibleTimePatterns?: boolean;
  entityId?: string;
  type?: string;
}

export interface OpenShiftsFilterInput {
  startTime: string;
  endTime: string;
  sort: 'time' | 'nearest' | 'bestFit';
  sortOrder: 'ASC' | 'DESC';
  timezone?: string;
}

export type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export interface OpenShiftPayload {
  pattern: {
    dayOfWeek: DayOfWeek;
    startTime: string;
    endTime: string;
  };
  sort: 'time' | 'nearest' | 'bestFit';
  sortOrder: 'ASC' | 'DESC';
  limit?: number;
  offset?: number;
  timezone: string;
  startTime: string;
  endTime: string;
}

export type PatientDetails = {
  skills: string[];
  services: string[];
  location: {
    city: string;
    state: string;
    distance: number;
  };
};

export interface OpenShiftByPatientEntity {
  bonus: string | null;
  endTime: string;
  id: string;
  startTime: string;
  services: string[];
  skills: string[];
  tags: string[];
  pendingRequestId: string | null;
  type: string;
}

export interface BulkUserCreationResponse {
  parsedData: Array<{
    user: {
      email: string;
      firstName: string;
      lastName: string;
      role: string;
    };
    entities: Array<string>;
    jobRole: string;
    role: string;
    location: string;
    permissions: Array<EmployeePermissions>;
  }>;
  errors: Array<{ message: string; error: string }>;
}

export type CapacityRequestCapacityPayload = {
  max_clinicians?: number | null;
};

export interface EntityMaxClinicianSettingsPayload {
  timezone: string;
  dayOfWeek: string;
  dateToCheck: string;
}

export interface EntityMaxCliniciansSettingsResponse {
  maxCliniciansSettings: {
    [dayOfWeek: string]: Array<{
      startTime: string;
      endTime: string;
      maxClinicians: number | null;
      currentClinicians: number;
    }>;
  };
}

export interface EntityEmploymentArchivePayload {
  isEmployeePageArchive: boolean;
  entityEmploymentId: string;
}

export interface EntityEmploymentArchiveResponse {
  id: string;
  location_id: string;
  job_role_id: string;
  employee_id: string;
  employee_status: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  source: string;
}

export type ShiftRequestWithHistory = {
  id: string;
  employee_id: string;
  calendar_id: string;
  request_type: string;
  request_status: 'pending' | 'approved' | 'rejected';
  requested_to_id: string;
  reason: string;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  updated_by: string;
  group_id: string;
  history: Array<{
    id: string;
    reason: string;
    groupId: string;
    createdAt: string;
    updatedAt: string;
    updatedBy: string | null;
    archivedAt: string | null;
    calendarId: string;
    employeeId: string;
    requestType: string;
    requestStatus: string;
    requestedToId: string;
  }>;
};

export interface BulkApproveShiftsResponse {
  success: boolean;
  approvedCount: number;
  shiftRequests: Array<ShiftRequestWithHistory>;
  errors: Array<{ startTime: string; endTime: string }>;
}

export interface BulkRejectShiftsResponse {
  success: boolean;
  rejectedCount: number;
  shiftRequests: Array<ShiftRequestWithHistory>;
  errors: Array<{ startTime: string; endTime: string }>;
}

export interface ResourcePermission {
  resource: string;
  level: string;
  name: string;
  description: string | null;
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface EmployeeRolePermission {
  id: string;
  employee_id: string;
  role_id: string;
  resource: string;
  level: string;
}

export interface EmployeeRoleResourcePermissionResponse {
  employeeId: string;
  roleId: string;
  permissions: EmployeeRolePermission[];
}

export interface UpdateEmployeeResourcePermissionsPayload {
  employeeId: string;
  roleId: string;
  permissionIds: string[];
}

export interface CreateAttributePolicyPayload {
  attributePolicy: {
    objectType: string;
    objectId: string;
    attributeName: string;
    attributeValues: string[];
  };
}
export interface CreateAttributePolicyResponse {
  id: string;
  objectType: string;
  objectId: string;
  attributeName: string;
  attributeValues: string[];
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  attributeValueNames?: string[];
}

export interface AttributePermissions {
  attributePermissions: CreateAttributePolicyResponse[];
}

export interface ManagedEmployeeResponse {
  id: string;
  entityId: string;
  firstName: string;
  lastName: string;
  managerId: string;
  title: string | null;
  userId: string;
}

export interface UpdateAttributePolicyPayload {
  attributeName?: string;
  attributeValues?: string[];
}

export interface IntakeQueueSocRequest {
  id: string;
  status: string;
  priority: string;
  entityId: string;
  createdAt: string;
  episodeId: string;
  patientId: string;
  updatedAt: string;
  detectedAt: string;
  externalId: string;
  socActualDatetime: string;
  socPlottedDatetime: string;
  socVisitAuthorizedAt: string | null;
  socVisitRequiresApproval: boolean;
}

export interface IntakeQueueEpisode {
  id: string;
  status: string;
  endDate: string;
  createdAt: string;
  patientId: string;
  startDate: string;
  updatedAt: string;
  externalId: string;
}

export interface IntakeQueueCarePlan {
  id: string;
  status: string;
  authorId: string | null;
  entityId: string;
  createdAt: string;
  episodeId: string;
  patientId: string;
  updatedAt: string;
  externalId: string;
  approvalExternalId: string | null;
  approvalReceivedAt: string | null;
  submittedForApprovalAt: string | null;
}

export interface IntakeQueueCarePlanLineItem {
  id: string;
  endDate: string;
  createdAt: string;
  startDate: string;
  updatedAt: string;
  externalId: string;
  carePlanId: string;
  frequencyType: string;
  inactivatedAt: string | null;
  preferredDays: string[];
  requiredSkills: string[];
  durationMinutes: number;
  frequencyStatus: string;
  visitsCompleted: number;
  visitsRemaining: number;
  frequencyInterval: string;
  visitsPerInterval: number;
  jobRoleCategoryId: string;
  totalAuthorizedVisits: number;
  insuranceApprovalStatus: string;
  insuranceAuthorizationApprovalAt: string | null;
}

export interface IntakeQueuePatientMetadata {
  empi?: string;
  address?: {
    city: string;
    state: string;
    zipcode: string;
    addressLine1: string;
  };
  insurance?: string;
  episodeId?: string;
  dateOfBirth?: string;
  startOfCare?: string;
  referringPhysician?: string;
}

export interface IntakeQueuePatient {
  id: string;
  firstName: string;
  lastName: string;
  entityId: string;
  address: string;
  encodedAddress: string | null;
  metadata: IntakeQueuePatientMetadata;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  externalId: string;
  socRequests: IntakeQueueSocRequest[];
  episodes: IntakeQueueEpisode[];
  carePlans?: IntakeQueueCarePlan[];
  carePlanLineItems?: IntakeQueueCarePlanLineItem[];
}

export interface IntakeQueueFilters {
  status?: string[];
  priority?: string[];
  search?: string;
  entityId?: string;
  startDate?: string;
  endDate?: string;
}

export interface ICustomersRouteResponse {
  employeeId: string;
  employeeAddress: {
    latitude: number;
    longitude: number;
  };
  routeData: {
    shift_sequence: string[];
    total_distance_miles: number;
    total_travel_minutes: number;
    total_work_minutes: number;
    legs: Array<{
      from_shift_id: string | null;
      to_shift_id: string | null;
      distance_miles: number;
      travel_minutes: number;
    }>;
    score: number;
  };
  shifts: Array<{
    id: string;
    startTime: string;
    endTime: string;
    entityId: string;
    customer: {
      actualEndTime: string | null;
      actualStartTime: string | null;
      id: string;
      name: string;
      address: string;
      encodedAddress: string | null;
      location: string | null;
      incomingLeg: {
        distanceMiles: number;
        fromShiftId: string;
        travelMinutes: number;
      };
      outgoingLeg: {
        distanceMiles: number;
        toShiftId: string;
        travelMinutes: number;
      };
    };
  }>;
}

export type PatientGroup = {
  entityId: string;
  fullName: string;
  type: string;
  distance: number | null;
  lastTimeWorked: string | null;
  pendingRequestId: string | null;
  opportunities: Array<{
    pattern: {
      weekday: string;
      startTime: string;
      endTime: string;
      dateRange: {
        startDate: string;
        endDate: string;
      };
    };
    skills: string[];
    bonusAvailable: boolean;
    shiftsScheduled: number;
    pendingAcknowledgements: number;
    entityId: string;
    entityName: string;
  }>;
  totals: {
    shiftsScheduled: number;
    pendingAcknowledgements: number;
  };
};

export type PatientShift = {
  id: string;
  startTime: string;
  endTime: string;
  services: string[];
  skills: string[];
  tags: string[];
  type: string;
  shiftRequest: {
    id: string;
    calendarId: string;
    status: 'approved' | 'pending' | 'rejected' | 'awaiting_acknowledgement';
    createdAt: string;
    type: string;
  };
};

export interface ShiftRequestMyShifts {
  shiftId: string;
  status: string;
  createdAt: string;
  type: string;
}

export interface ConfirmPatientShiftsPayload {
  shiftRequests: {
    id: string;
    calendarId: string;
    requestType: 'apply';
    requestStatus: string;
    reason?: string;
  }[];
}

export type BulkEditShiftsInput = {
  shiftIds: string[];
  operations?: {
    changeEntity?: {
      entityId: string;
    };
    changeType?: {
      type: string;
    };
    changeStates?: {
      licensedStates: string[];
    };
    changeBonus?: {
      bonus: number;
    };
    tags?: {
      add?: string[];
      remove?: string[];
    };
    changeDescription?: {
      description: string;
    };
    archive?: boolean;
  };
  dryRun: boolean;
};

export type BulkEditShiftsOutput = {
  results: Array<{
    shiftId: string;
    status: 'success' | 'error' | 'warning';
    warnings: string[];
    error?: string | null;
    operations: Record<string, any>;
  }>;
  summary: {
    failed: number;
    skipped: number;
    succeeded: number;
    total: number;
    warnings: number;
  };
};

export type ShiftDetailsByIdsOutput = {
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  shifts: Array<{
    employee?: {
      id: string;
      fullName: string;
    };
    endTime: string;
    entity: { id: string; name: string };
    id: string;
    startTime: string;
  }>;
};
